<template>
  <canvas ref="canvas" />
</template>

<script setup lang="ts">
  import { onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';

  import colors from '@/Colors';

  interface IGridOverlayProps {
    cellWidth: number;
    cellHeight: number;
    pixelRatio: number;
    showQuarterLines?: boolean;
  }

  const props = defineProps<IGridOverlayProps>();

  const canvas = useTemplateRef('canvas');
  const isGameActive = ref(false);
  const canInputNewKeyStroke = ref(true);
  const snake = ref([{ x: 12, y: 3 }]);
  const direction = ref({ x: 1, y: 0 });
  const bufferedDirection = ref({ x: 0, y: 0 });
  const food = ref({ x: 1, y: 1 });
  const gameInterval = ref<number | null>(null);
  const skullInterval = ref<number | null>(null);
  const score = ref(0);

  enum GridOverlayGameState {
    STARTED = 'game-started',
    ENDED = 'game-ended',
  }

  interface IEmits {
    (e: GridOverlayGameState.STARTED, value: boolean): void;
    (e: GridOverlayGameState.ENDED, value: boolean): void;
  }

  const emits = defineEmits<IEmits>();

  const konamiCode = [
    'ArrowUp',
    'ArrowUp',
    'ArrowDown',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowLeft',
    'ArrowRight',
    'b',
    'a',
  ];
  let konamiCodePosition = 0;

  const numberMatrices = {
    1: [
      [0, 1, 0],
      [1, 1, 0],
      [0, 1, 0],
      [0, 1, 0],
      [1, 1, 1],
    ],
    2: [
      [1, 1, 1],
      [0, 0, 1],
      [1, 1, 1],
      [1, 0, 0],
      [1, 1, 1],
    ],
    3: [
      [1, 1, 1],
      [0, 0, 1],
      [1, 1, 1],
      [0, 0, 1],
      [1, 1, 1],
    ],
    4: [
      [1, 0, 1],
      [1, 0, 1],
      [1, 1, 1],
      [0, 0, 1],
      [0, 0, 1],
    ],
    5: [
      [1, 1, 1],
      [1, 0, 0],
      [1, 1, 1],
      [0, 0, 1],
      [1, 1, 1],
    ],
    6: [
      [1, 1, 1],
      [1, 0, 0],
      [1, 1, 1],
      [1, 0, 1],
      [1, 1, 1],
    ],
    7: [
      [1, 1],
      [0, 1],
      [0, 1],
      [0, 1],
      [0, 1],
    ],
    8: [
      [1, 1, 1],
      [1, 0, 1],
      [1, 1, 1],
      [1, 0, 1],
      [1, 1, 1],
    ],
    9: [
      [1, 1, 1],
      [1, 0, 1],
      [1, 1, 1],
      [0, 0, 1],
      [0, 0, 1],
    ],
    0: [
      [1, 1, 1],
      [1, 0, 1],
      [1, 0, 1],
      [1, 0, 1],
      [1, 1, 1],
    ],
  };

  function clearCanvas() {
    if (!canvas.value) return;
    const ctx = canvas.value.getContext('2d')!;
    if (ctx) {
      ctx.clearRect(0, 0, canvas.value!.width, canvas.value!.height);
    }
  }

  function drawGrid() {
    clearCanvas();
    const ctx = canvas.value!.getContext('2d')!;
    canvas.value!.width = canvas.value!.offsetWidth * props.pixelRatio;
    canvas.value!.height = canvas.value!.offsetHeight * props.pixelRatio;
    ctx.strokeStyle = '#eee';
    ctx.lineWidth = props.pixelRatio;
    for (let d = 1; d <= 7; d += 1) {
      const rowYPosition = props.cellHeight * d * props.pixelRatio;
      ctx.beginPath();
      ctx.moveTo(0, rowYPosition);
      ctx.lineTo(canvas.value!.width, rowYPosition);
      ctx.stroke();
      ctx.closePath();
    }
    for (let h = 1; h <= 24; h += 1) {
      const colXPosition = props.cellWidth * h * props.pixelRatio;
      ctx.beginPath();
      ctx.moveTo(colXPosition, 0);
      ctx.lineTo(colXPosition, canvas.value!.height * props.pixelRatio);
      ctx.stroke();
      ctx.closePath();
    }

    if (props.showQuarterLines) {
      ctx.strokeStyle = '#eeeeee7d';
      for (let h = 0; h <= 24; h += 1) {
        const colXPosition = props.cellWidth * h * props.pixelRatio;

        for (let q = 1; q < 4; q += 1) {
          const quarterXPosition = colXPosition + (props.cellWidth / 4) * q * props.pixelRatio;
          ctx.beginPath();
          ctx.moveTo(quarterXPosition, 0);
          ctx.lineTo(quarterXPosition, canvas.value!.height * props.pixelRatio);
          ctx.stroke();
          ctx.closePath();
        }
      }
    }
  }

  function drawSnake() {
    const ctx = canvas.value!.getContext('2d')!;
    ctx.fillStyle = colors.success;
    snake.value.forEach((segment) => {
      ctx.fillRect(
        segment.x * props.cellWidth * props.pixelRatio,
        segment.y * props.cellHeight * props.pixelRatio,
        props.cellWidth * props.pixelRatio,
        props.cellHeight * props.pixelRatio,
      );
    });
  }

  function drawFood() {
    const ctx = canvas.value!.getContext('2d')!;
    ctx.fillStyle = colors.primary;
    ctx.fillRect(
      food.value.x * props.cellWidth * props.pixelRatio,
      food.value.y * props.cellHeight * props.pixelRatio,
      props.cellWidth * props.pixelRatio,
      props.cellHeight * props.pixelRatio,
    );
  }

  function drawCountdown(number: number) {
    clearCanvas();
    drawGrid();
    const currentMatrix = numberMatrices[number];
    const ctx = canvas.value!.getContext('2d')!;
    ctx.fillStyle = colors.success;
    currentMatrix.forEach((row, rowIndex) => {
      row.forEach((cell, cellIndex) => {
        if (cell) {
          ctx.fillRect(
            (cellIndex + 10) * props.cellWidth * props.pixelRatio,
            (rowIndex + 1) * props.cellHeight * props.pixelRatio,
            props.cellWidth * props.pixelRatio,
            props.cellHeight * props.pixelRatio,
          );
        }
      });
    });
  }

  function drawSkull() {
    const skullMatrix = [
      [0, 1, 1, 1, 1, 1, 0],
      [1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1],
      [1, 0, 0, 1, 0, 0, 1],
      [1, 0, 0, 1, 0, 0, 1],
      [0, 1, 1, 0, 1, 1, 0],
      [0, 0, 1, 1, 1, 0, 0],
    ];
    const ctx = canvas.value!.getContext('2d')!;
    ctx.fillStyle = colors.error;
    skullMatrix.forEach((row, rowIndex) => {
      row.forEach((cell, cellIndex) => {
        if (cell) {
          ctx.fillRect(
            (cellIndex + 9) * props.cellWidth * props.pixelRatio,
            rowIndex * props.cellHeight * props.pixelRatio,
            props.cellWidth * props.pixelRatio,
            props.cellHeight * props.pixelRatio,
          );
        }
      });
    });
  }

  function drawScore() {
    clearCanvas();
    drawGrid();
    const ctx = canvas.value!.getContext('2d')!;
    ctx.fillStyle = colors.success;
    const scoreAsString = `${score.value}`;
    const letfPadding = Math.round((24 - (scoreAsString.length * 4 - 1)) / 2);
    scoreAsString.split('').forEach((digit, index) => {
      const currentMatrix = numberMatrices[parseInt(digit)];
      currentMatrix.forEach((row, rowIndex) => {
        row.forEach((cell, cellIndex) => {
          if (cell) {
            ctx.fillRect(
              (cellIndex + letfPadding + index * 4) * props.cellWidth * props.pixelRatio,
              (rowIndex + 1) * props.cellHeight * props.pixelRatio,
              props.cellWidth * props.pixelRatio,
              props.cellHeight * props.pixelRatio,
            );
          }
        });
      });
    });
  }

  function updateGame() {
    const head = { x: snake.value[0].x + direction.value.x, y: snake.value[0].y + direction.value.y };

    // Check for collisions with walls or self
    if (
      head.x < 0 ||
      head.x >= 24 ||
      head.y < 0 ||
      head.y >= 7 ||
      snake.value.some((segment) => segment.x === head.x && segment.y === head.y)
    ) {
      stopGame();
      return;
    }

    snake.value.unshift(head);
    if (head.x === food.value.x && head.y === food.value.y) {
      score.value += 10; // Increase score
      const validSpots = [];
      for (let x = 0; x < 24; x++) {
        for (let y = 0; y < 7; y++) {
          if (!snake.value.some((segment) => segment.x === x && segment.y === y)) {
            validSpots.push({ x, y });
          }
        }
      }
      food.value = validSpots[Math.floor(Math.random() * validSpots.length)];
    } else {
      snake.value.pop();
    }
    drawGrid();
    drawSnake();
    drawFood();

    canInputNewKeyStroke.value = true;
    if (bufferedDirection.value.x !== 0 || bufferedDirection.value.y !== 0) {
      direction.value = bufferedDirection.value;
      bufferedDirection.value = { x: 0, y: 0 };
    }
  }

  async function startGame() {
    emits(GridOverlayGameState.STARTED, true);
    snake.value = [
      { x: 12, y: 3 },
      { x: 11, y: 3 },
      { x: 10, y: 3 },
      { x: 9, y: 3 },
    ]; // Reset snake position to the center
    direction.value = { x: 1, y: 0 }; // Reset direction
    score.value = 0; // Reset score
    for (let i = 3; i > 0; i--) {
      drawCountdown(i);
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    canInputNewKeyStroke.value = true;
    isGameActive.value = true;
    gameInterval.value = setInterval(updateGame, 200) as unknown as number;
  }

  function stopGame() {
    if (!isGameActive.value) {
      return;
    }
    if (gameInterval.value) {
      clearInterval(gameInterval.value);
      gameInterval.value = null;
    }
    clearCanvas();
    drawGrid();
    drawSkull();

    // Flashing skull effect
    let isSkullVisible = false;
    skullInterval.value = setInterval(() => {
      clearCanvas();
      drawGrid();
      if (isSkullVisible) {
        drawSkull();
      }
      isSkullVisible = !isSkullVisible;
    }, 500) as unknown as number;

    setTimeout(() => {
      clearInterval(skullInterval.value!);
      skullInterval.value = null;
      clearCanvas();
      drawGrid();
      drawScore(); // Display score after flashing skull
      isGameActive.value = false;

      setTimeout(() => {
        closeGame();
      }, 5000); // Close game after 5 seconds
    }, 3000);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (isGameActive.value) {
      event.preventDefault();
      event.stopPropagation();
    }
    let dir = { x: 0, y: 0 };
    if (event.key === 'ArrowUp') dir = { x: 0, y: -1 };
    if (event.key === 'ArrowDown') dir = { x: 0, y: 1 };
    if (event.key === 'ArrowLeft') dir = { x: -1, y: 0 };
    if (event.key === 'ArrowRight') dir = { x: 1, y: 0 };
    if (canInputNewKeyStroke.value) {
      direction.value = dir;
      canInputNewKeyStroke.value = false;
    } else {
      bufferedDirection.value = dir;
    }
  }

  function closeGame() {
    clearCanvas();
    drawGrid();
    emits(GridOverlayGameState.ENDED, true);
  }

  function handleKeyCombination(event: KeyboardEvent) {
    if (!isGameActive.value) {
      closeGame();
    }

    if (event.key === konamiCode[konamiCodePosition]) {
      konamiCodePosition++;
      if (konamiCodePosition === konamiCode.length) {
        startGame();
        konamiCodePosition = 0;
      }
    } else {
      konamiCodePosition = 0;
    }
  }

  watch(() => props.cellHeight, drawGrid);
  watch(() => props.cellWidth, drawGrid);
  watch(() => props.pixelRatio, drawGrid);

  onMounted(() => {
    drawGrid();
    window.addEventListener('keydown', handleKeyCombination);
    window.addEventListener('keydown', handleKeyDown);
  });

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyCombination);
    window.removeEventListener('keydown', handleKeyDown);
    if (isGameActive.value) {
      stopGame();
    }
  });
</script>

<style lang="scss" scoped>
  canvas {
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
</style>
